import React, { useEffect, useState, useRef } from "react";
import {
  CompareMatrixSectionType,
  CompareMatrixSectionFeatureType,
} from "../types/CompareMatrixPricing";
import Tooltip from "../Tooltip/Tooltip";
import * as Collapsible from "@radix-ui/react-collapsible";
import { AnimatePresence, motion } from "framer-motion";
import Check from "../SVGs/Check";
import Chevron from "../SVGs/Chevron";

const CompareMatrixSection: React.FC<CompareMatrixSectionType> = ({
  sectionTitle,
  sectionIcon,
  priceData,
  offer,
  features,
}) => {
  const [open, setOpen] = useState(true);
  const headerRef = useRef<HTMLDivElement>(null);
  const totalFeatures = features.length;

  useEffect(() => {
    if (headerRef.current) {
      const rect = headerRef?.current.getBoundingClientRect();

      if (rect.top < 0 && !open) {
        headerRef.current?.scrollIntoView({
          behavior: "instant",
          block: "start",
        });
      }
    }
  }, [open]);

  const featuresAnimateVariants = {
    hidden: { opacity: 0, translateY: 6, height: 0 },
    visible: {
      opacity: 1,
      translateY: 0,
      height: "100%",
      transition: { duration: 0.1, delay: 0.3 },
    },
    exit: {
      opacity: 0,
      translateY: 6,
      height: 0,
      transition: { duration: 0.1 },
    },
  };

  return (
    <div
      ref={headerRef}
      className={`compare-section compare-matrix-pricing__font-set scroll-mt-[88px] md:scroll-mt-[56px] mt-sm pt-4 border-t-md border-forest-400 transition duration-200 delay-75 first:border-0 first:mt-0 first:pt-0 ${
        open ? "border-white open" : ""
      }`}
    >
      <Collapsible.Root
        className="CollapsibleRoot"
        open={open}
        onOpenChange={setOpen}
      >
        <div className="flex justify-between items-center sticky z-20 md:top-[56px] top-[88px] pt-6">
          <Collapsible.Trigger asChild>
            <button className="compare-section__header compare-section__header--desktab md:max-w-[40%] min-w-[240px] md:min-w-[211px] bg-transparent">
              <div className="iconButton flex flex-wrap items-center lg:pr-0 pr-xs">
                <img
                  className="w-6 h-6 self-center mr-xxxxs"
                  src={sectionIcon.icon}
                  alt={sectionIcon.iconAltText}
                />
                <h3 className="compare-matrix-pricing__font-set md:text-lg body--text-extra-large pr-xxxxs">
                  {sectionTitle}
                </h3>
                <span className="compare-matrix-pricing__font-set sr-only">{`${
                  open ? "Collapse" : "Expand"
                }`}</span>
                <Chevron
                  className={`chevron transition-transform motion-reduce:transition-none ${
                    open ? "chevon--active rotate-180" : "rotate-0"
                  }`}
                  aria-hidden="true"
                />
              </div>
            </button>
          </Collapsible.Trigger>
          <AnimatePresence initial={false}>
            {!open && (
              <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={featuresAnimateVariants}
              >
                <div className="compare-section__features compare-matrix-pricing__font-set bg-forest-200 pt-1 pb-0.5 px-3 rounded-circle">
                  {totalFeatures} features
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <Collapsible.Content forceMount>
          <AnimatePresence initial={true}>
            {open && (
              <motion.div
                initial={{ opacity: 0, translateY: 15 }}
                animate={{ opacity: 1, translateY: 0 }}
                exit={{ opacity: 0, translateY: 25 }}
                transition={{ duration: 0.1, delay: 0.1 }}
              >
                <div className="w-full -mt-[59px]" key="content">
                  <div
                    className={`compare-section__header md:gap-xxs bg-white z-10 flex gap-xs ${
                      open ? "sticky" : ""
                    } md:top-[56px] top-[88px] after:content-[''] after:absolute after:top-0 after:h-full after:w-5 after:bg-white after:-left-4 before:content-[''] before:absolute before:top-0 before:h-full before:w-5 before:bg-white before:-right-4`}
                  >
                    <div className="compare-section__plan-info md:first:basis-[40%] w-1/4"></div>
                    {priceData?.map((price, key) => {
                      const { name, orgPrice, discountPrice } = price;
                      const discountPriceString = discountPrice.split(".");
                      const dollars = discountPriceString[0];
                      const cents = discountPriceString[1];

                      return (
                        <div
                          className="compare-section__plan-info bg-white relative z-30 w-1/4"
                          key={key}
                        >
                          <div className="compare-section__price-info md:p-xxs flex-1 flex flex-wrap items-center justify-between h-full p-xs border border-forest-400 rounded-t-xxs bg-white">
                            <span className="compare-section__plan compare-matrix-pricing__font-set md:text-lg md:font-medium md:basis-full basis-[98px] pr-1 body--text-extra-large">
                              {name}
                            </span>
                            {offer === "buynow" ? (
                              <div className="basis-[135px]">
                                <span className="compare-section__price compare-matrix-pricing__font-set md:text-base body--text-large text-forest-700 mr-2.5 relative after:content-[''] after:h-px after:absolute after:top-1/2 after:bg-forest-700 after:w-[calc(100%+4px)] after:left-0">
                                  ${orgPrice}
                                </span>
                                <span className="compare-section__price compare-matrix-pricing__font-set md:text-base body--text-large">
                                  ${dollars}
                                  <span className="md:text-[0.625rem] md:-top-1 text-sm relative left-px -top-[5px]">
                                    {cents}
                                  </span>
                                  /mo
                                </span>
                              </div>
                            ) : (
                              <span className="compare-section__price compare-matrix-pricing__font-set md:text-base body--text-large">
                                ${orgPrice}/mo
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="md:gap-xxs flex relative gap-xs">
                    {[0, 1, 2, 3].map((_, key) => {
                      return (
                        <div
                          key={key}
                          className="compare-row__cell-item md:first:basis-[calc(40%+1px)] md:first:border-0 md:first:w-full first:border-transparent basis-1/4 last:mr-0 border-l border-r pb-6 border-forest-400"
                        ></div>
                      );
                    })}
                  </div>
                  <div className="compare-section__row">
                    {features?.map(
                      (
                        feature: CompareMatrixSectionFeatureType,
                        key: number
                      ) => {
                        const { name, tooltip, addNewBadge, row1, row2, row3 } =
                          feature;
                        const rows = [row1, row2, row3];

                        return (
                          <div
                            className="compare-row__item md:after:hidden md:gap-xxs flex gap-xs w-full min-h-16 relative z-[1] hover-never:after:opacity-100 after:content-[''] after:block after:absolute after:h-15 after:w-[calc(100%+20px)] after:top-0 after:left-[-10px] after:right-0 after:bottom-0 after:-z-10 after:bg-sun-200 after:opacity-0 after:transition-opacity after:rounded-xxs"
                            key={key}
                          >
                            <div className="md:basis-[40%] flex items-center basis-1/4">
                              <span className="compare-row__info inline">
                                <span
                                  className={`compare-row__heading compare-matrix-pricing__font-set ${
                                    tooltip ? "mr-1.5" : "mr-2"
                                  } `}
                                >
                                  {name}
                                </span>
                                <div className="inline-block">
                                  {tooltip && (
                                    <span className="compare-row__tooltip inline-block whitespace-nowrap mr-xxxxs relative -left-1.5">
                                      <Tooltip copy={tooltip} />
                                    </span>
                                  )}
                                  {addNewBadge && (
                                    <span
                                      className={`compare-row__badge compare-matrix-pricing__font-set body--text-eyebrow pt-[5px] pb-1 px-1.5 bg-sky-500 rounded-xxs inline-block whitespace-nowrap relative ${
                                        tooltip ? "-left-3" : ""
                                      }`}
                                    >
                                      New
                                    </span>
                                  )}
                                </div>
                              </span>
                            </div>
                            {rows?.map((row, key) => {
                              return (
                                <div
                                  className="compare-row__cell basis-1/4"
                                  key={key}
                                >
                                  <div className="compare-row__cell-item md:p-xxs flex items-center justify-center border-x border-forest-400 h-full px-xs">
                                    {row.cellItem === "Check" ? (
                                      <span className="compare-row__check flex items-center justify-center">
                                        <Check />
                                      </span>
                                    ) : row.cellItem === "Add on" ? (
                                      <span className="compare-row__add-on compare-matrix-pricing__font-set md:text-[13px] flex items-center justify-center text-center">
                                        {row.addOnText}
                                      </span>
                                    ) : (
                                      <span className="flex"></span>
                                    )}
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        );
                      }
                    )}
                    <div className="md:gap-xxs flex relative gap-xs">
                      {[0, 1, 2, 3].map((_, key) => {
                        return (
                          <div
                            key={key}
                            className="compare-row__bottom compare-row__cell-item md:first:basis-[calc(40%+1px)] md:first:border-0 md:first:w-full first:border-transparent basis-1/4 last:mr-0 border-b border-l border-r pb-6 border-forest-400 rounded-b-xxs"
                          ></div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
};

export default CompareMatrixSection;
